import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

const NOTE_TO_SEMITONE = {
  C: 0,
  'C#': 1,
  D: 2,
  'D#': 3,
  E: 4,
  F: 5,
  'F#': 6,
  G: 7,
  'G#': 8,
  A: 9,
  'A#': 10,
  B: 11,
};

function noteNameToFrequency(noteName) {
  const match = /^([A-G]#?)(-?\d+)$/.exec(String(noteName || '').trim());
  if (!match) return null;
  const pitchClass = match[1];
  const octave = Number(match[2]);
  const semitone = NOTE_TO_SEMITONE[pitchClass];
  if (typeof semitone !== 'number' || Number.isNaN(octave)) return null;
  const midi = (octave + 1) * 12 + semitone;
  return 440 * (2 ** ((midi - 69) / 12));
}

function stopActiveAudio(activeNodesRef) {
  for (const node of activeNodesRef.current) {
    try {
      if (typeof node.stop === 'function') node.stop();
    } catch (e) {}
    try {
      if (typeof node.disconnect === 'function') node.disconnect();
    } catch (e) {}
  }
  activeNodesRef.current = [];
}

function playAudioSpec(audioContext, audioSpec, activeNodesRef) {
  if (!audioContext || !audioSpec || !Array.isArray(audioSpec.notes) || audioSpec.notes.length === 0) return;

  stopActiveAudio(activeNodesRef);

  const now = audioContext.currentTime + 0.03;
  const baseWaveform = audioSpec.waveform || 'triangle';
  const playbackStyle = String(audioSpec.playbackStyle || 'Blocked');
  const resolvedStyle = playbackStyle === 'Mixed'
    ? (Math.random() < 0.5 ? 'Blocked' : 'Arpeggio')
    : playbackStyle;
  const durationSeconds = Math.max(0.8, (audioSpec.durationMs || 1600) / 1000);
  const noteGapSeconds = Math.max(0, (audioSpec.noteGapMs || 120) / 1000);

  audioSpec.notes.forEach((noteName, index) => {
    const frequency = noteNameToFrequency(noteName);
    if (!frequency) return;

    const startAt = now + (resolvedStyle === 'Arpeggio' ? noteGapSeconds * index : 0);
    const stopAt = startAt + durationSeconds + 0.25;
    const filter = audioContext.createBiquadFilter();
    const gain = audioContext.createGain();
    const mainOscillator = audioContext.createOscillator();
    const overtoneOscillator = audioContext.createOscillator();

    filter.type = 'lowpass';
    filter.frequency.value = 2400;
    filter.Q.value = 0.25;

    mainOscillator.type = baseWaveform;
    mainOscillator.frequency.setValueAtTime(frequency, startAt);

    overtoneOscillator.type = 'sine';
    overtoneOscillator.frequency.setValueAtTime(frequency * 2, startAt);

    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(0.12, startAt + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.08, startAt + Math.max(0.2, durationSeconds * 0.6));
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSeconds + 0.18);

    mainOscillator.connect(filter);
    overtoneOscillator.connect(filter);
    filter.connect(gain);
    gain.connect(audioContext.destination);

    mainOscillator.start(startAt);
    overtoneOscillator.start(startAt);
    mainOscillator.stop(stopAt);
    overtoneOscillator.stop(stopAt);

    activeNodesRef.current.push(mainOscillator, overtoneOscillator, filter, gain);
  });
}

function formatAnswerTime(milliseconds) {
  if (milliseconds === undefined || milliseconds === null) return null;
  const seconds = milliseconds / 1000;
  return `${seconds.toFixed(2)}s`;
}

function MusicTheoryQuizGame({ gameState, gamePlayers, user, socket, roomId }) {
  const { submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = useMemo(() => normalizedState || gameState || {}, [normalizedState, gameState]);
  const meId = user ? (user.socketId || user.userId) : null;
  const audioContextRef = useRef(null);
  const activeNodesRef = useRef([]);
  const countdownFrameRef = useRef(null);
  const lastAutoPlayRoundRef = useRef(null);

  const [myAnswer, setMyAnswer] = useState({ value: null, round: -1 });
  const [audioReady, setAudioReady] = useState(false);
  const [audioError, setAudioError] = useState('');
  const [timeLeft, setTimeLeft] = useState({ value: state.timeLimit || 15, round: -1 });

  const currentRound = state.roundIndex ?? -1;
  const timeLimit = state.timeLimit || 15;
  const currentQuestion = state.current || null;
  const currentAudioSpec = (currentQuestion && currentQuestion.audioSpec) || state.audioSpec || null;
  const myAnswerValue = myAnswer.round === currentRound ? myAnswer.value : null;

  const ensureAudioContext = async () => {
    if (typeof window === 'undefined') return null;
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) {
      setAudioError('This browser does not support Web Audio.');
      return null;
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioCtor();
    }

    try {
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      setAudioReady(audioContextRef.current.state === 'running');
      setAudioError('');
      return audioContextRef.current;
    } catch (error) {
      setAudioError('Audio is blocked until the page receives a user interaction.');
      return null;
    }
  };

  const playCurrentClue = async () => {
    const context = await ensureAudioContext();
    if (!context || !currentAudioSpec) return;
    playAudioSpec(context, currentAudioSpec, activeNodesRef);
  };

  const handleAnswer = (option) => {
    if (state.finished || state.phase !== 'question' || myAnswerValue) return;
    setMyAnswer({ value: option, round: state.roundIndex });
    submitAction({ type: 'answer', answer: option }).catch(() => {});
  };

  useEffect(() => {
    let serverAnswer = null;
    if (state.answers && typeof state.answers === 'object' && meId && Object.prototype.hasOwnProperty.call(state.answers, meId)) {
      serverAnswer = state.answers[meId];
    }

    if (serverAnswer !== null && serverAnswer !== undefined) {
      setMyAnswer({ value: serverAnswer, round: state.roundIndex });
      return;
    }

    setMyAnswer({ value: null, round: state.roundIndex });
  }, [state.answers, state.roundIndex, meId]);

  useEffect(() => {
    setTimeLeft({ value: timeLimit, round: state.roundIndex });
  }, [state.roundIndex, timeLimit]);

  useEffect(() => {
    if (countdownFrameRef.current) {
      cancelAnimationFrame(countdownFrameRef.current);
      countdownFrameRef.current = null;
    }

    if (state.phase !== 'question' || state.finished) return undefined;

    const start = Date.now();
    const tick = () => {
      const elapsed = (Date.now() - start) / 1000;
      setTimeLeft({ value: Math.max(0, Math.ceil(timeLimit - elapsed)), round: state.roundIndex });
      if (elapsed < timeLimit && state.phase === 'question' && !state.finished) {
        countdownFrameRef.current = requestAnimationFrame(tick);
      }
    };

    countdownFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (countdownFrameRef.current) cancelAnimationFrame(countdownFrameRef.current);
    };
  }, [state.phase, state.finished, state.roundIndex, timeLimit]);

  useEffect(() => {
    if (!audioReady || !currentAudioSpec || state.phase !== 'question') return undefined;
    if (lastAutoPlayRoundRef.current === state.roundIndex) return undefined;
    lastAutoPlayRoundRef.current = state.roundIndex;
    playAudioSpec(audioContextRef.current, currentAudioSpec, activeNodesRef);
    return undefined;
  }, [audioReady, currentAudioSpec, state.phase, state.roundIndex]);

  useEffect(() => () => {
    stopActiveAudio(activeNodesRef);
    if (countdownFrameRef.current) cancelAnimationFrame(countdownFrameRef.current);
    try {
      if (audioContextRef.current && typeof audioContextRef.current.close === 'function') {
        audioContextRef.current.close();
      }
    } catch (e) {}
  }, []);

  return (
    <div className="mx-auto max-w-3xl p-4 text-gray-900 dark:text-gray-100">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-500 dark:text-indigo-300">
            Ear Training
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Round {Math.min((state.roundIndex || 0) + 1, state.rounds || 0)} of {state.rounds || 0}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-[0.12em]">
          <span className="rounded-full bg-indigo-100 px-3 py-1 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200">
            {currentQuestion ? currentQuestion.category : 'Music'}
          </span>
          <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200">
            {state.difficulty || 'Easy'}
          </span>
        </div>
      </div>

      <div className="mb-5 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-xl">
            <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {currentQuestion ? currentQuestion.prompt : 'Waiting for the next clue...'}
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Listen carefully, then lock in the correct answer before the other players do.
            </p>
            {!audioReady ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Enable audio once and each new clue will replay automatically while you stay in the quiz.
              </p>
            ) : null}
            {audioError ? (
              <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">{audioError}</p>
            ) : null}
          </div>

          <div className="flex flex-col items-start gap-2 lg:items-end">
            <button
              type="button"
              onClick={playCurrentClue}
              className="rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              {audioReady ? 'Replay Clue' : 'Enable Audio'}
            </button>
            {currentAudioSpec ? (
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {currentAudioSpec.kind === 'chord' ? 'Chord clue' : 'Single note clue'}
                {' · '}
                {currentAudioSpec.waveform || state.waveform || 'triangle'} synth
              </div>
            ) : null}
          </div>
        </div>

        {state.phase === 'question' ? (
          <>
            <div className="mt-5 grid gap-3">
              {((currentQuestion && currentQuestion.options) || []).map((option) => {
                const correctAnswer = currentQuestion ? currentQuestion.correct : null;
                const isSelected = myAnswerValue !== null && String(myAnswerValue) === String(option);
                const isCorrect = correctAnswer && String(correctAnswer) === String(option);
                const answered = !!myAnswerValue;
                let buttonClass = 'bg-slate-100 text-slate-900 hover:bg-indigo-600 hover:text-white dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-indigo-500';

                if (isSelected) {
                  buttonClass = isCorrect
                    ? 'bg-green-600 text-white dark:bg-green-500'
                    : 'bg-red-600 text-white dark:bg-red-500';
                } else if (answered && isCorrect) {
                  buttonClass = 'bg-green-600 text-white dark:bg-green-500';
                }

                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleAnswer(option)}
                    disabled={state.finished || state.phase !== 'question' || answered}
                    className={`w-full rounded-2xl px-4 py-3 text-left text-sm font-medium transition-colors ${buttonClass} ${answered && !isSelected && !isCorrect ? 'opacity-65' : ''}`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>

            <div className="mt-5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
              <div
                className="h-3 bg-amber-400 transition-[width] duration-100"
                style={{ width: `${(1 - (timeLeft.value / timeLimit)) * 100}%` }}
              />
            </div>
            <div className="mt-2 text-right text-xs font-medium text-slate-500 dark:text-slate-400">
              {timeLeft.value}s left
            </div>
          </>
        ) : null}

        {state.phase === 'reveal' ? (
          <div className="mt-5 rounded-2xl bg-slate-50 p-4 dark:bg-slate-800/70">
            <div className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
              Reveal
            </div>
            <div className="mt-2 text-lg font-bold text-green-700 dark:text-green-400">
              Correct answer: {currentQuestion ? currentQuestion.correct : 'Unknown'}
            </div>
            {currentQuestion && currentQuestion.explanation ? (
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {currentQuestion.explanation}
              </p>
            ) : null}
            {/* {currentQuestion && currentQuestion.answerDetail ? (
              <div className="mt-3 text-xs font-medium uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                Detail: {currentQuestion.answerDetail}
              </div>
            ) : null} */}

            <div className="mt-4 space-y-2 text-sm">
              {(gamePlayers || []).map((player) => {
                const playerId = player.socketId || player.userId || player.id;
                const answer = state.answers && typeof state.answers === 'object' ? state.answers[playerId] : null;
                const gotCorrect = answer && currentQuestion && String(answer) === String(currentQuestion.correct);
                const timeString = formatAnswerTime(state.answerTimes && state.answerTimes[state.roundIndex] && state.answerTimes[state.roundIndex][playerId]);
                const award = state.roundAwards && state.roundAwards[playerId];

                return (
                  <div key={playerId} className="flex flex-wrap items-center gap-2 rounded-xl bg-white px-3 py-2 dark:bg-slate-900">
                    <span className="font-semibold">{player.username || playerId}</span>
                    <span className={gotCorrect ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                      {gotCorrect ? 'Correct' : (answer ? 'Wrong' : 'No answer')}
                    </span>
                    {answer ? <span className="text-xs text-slate-500 dark:text-slate-400">[{answer}]</span> : null}
                    {timeString ? <span className="text-xs text-slate-500 dark:text-slate-400">{timeString}</span> : null}
                    {typeof award === 'number' ? (
                      <span className="text-xs font-bold text-amber-600 dark:text-amber-300">+{award} pts</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {state.countdownToStart && !state.started ? (() => {
        const host = (state.players && state.players[0]) || null;
        const isHost = host && user && ((host.socketId && host.socketId === user.socketId) || (host.userId && host.userId === user.userId));
        if (!isHost) return null;
        return (
          <button
            type="button"
            className="rounded-full bg-green-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-700"
            onClick={() => submitAction({ type: 'start' })}
          >
            Start Music Theory Quiz
          </button>
        );
      })() : null}
    </div>
  );
}

MusicTheoryQuizGame.Guide = function MusicTheoryQuizGuide() {
  return (
    <div>
      <h3 className="mb-2 font-bold">Music Theory Quiz</h3>
      <p>Hear a note or chord, replay the clue if needed, and answer faster than everyone else to score the most points.</p>
    </div>
  );
};

export default MusicTheoryQuizGame;